import { fromZonedTime, toZonedTime } from 'date-fns-tz';
import { captureWarning } from './sentry';

const pad2 = (n) => String(n).padStart(2, '0');

/**
 * Build a merchant-local wall-clock ISO string ("YYYY-MM-DDTHH:mm:00") from a
 * calendar day and a fractional hour.
 *
 * Minutes are derived via total-minutes so a fractional hour that rounds to 60
 * (e.g. 9.999… from floating-point slot accumulation) carries into the hour
 * instead of emitting an invalid "HH:60:00" string. For valid inputs the output
 * is byte-identical to the previous per-field formatting.
 */
function buildWallClockString(baseDate, hourFloat) {
  const y = baseDate.getFullYear();
  const mo = pad2(baseDate.getMonth() + 1);
  const d = pad2(baseDate.getDate());
  const totalMin = Math.round(hourFloat * 60);
  const hh = pad2(Math.floor(totalMin / 60));
  const mm = pad2(totalMin % 60);
  return `${y}-${mo}-${d}T${hh}:${mm}:00`;
}

/**
 * Build the canonical UTC startDate for a booking.
 *
 * Immediate bookings: bookingData.startDate already holds the correct UTC
 * instant (set in BookingFlow via getCurrentUTCDate()). Return it untouched.
 *
 * Scheduled bookings: the user picked a calendar day (via DateSelection,
 * stored as browser-local midnight — browser-local Y/M/D matches the clicked
 * day) and a time slot whose label comes from the merchant's opening_hours
 * strings (merchant-local wall clock). We combine those two as a
 * merchant-local wall clock and convert to UTC via fromZonedTime so the
 * stored timestamp is correct regardless of the user's browser timezone.
 *
 * @param {Object} bookingData
 * @param {number|null} startTimeHours - Fractional hours (e.g. 11.75 for
 *   11:45). Ignored for immediate bookings; pass null.
 * @param {string|null} merchantTimezone - IANA tz (e.g. 'America/New_York').
 *   Falls back to 'UTC' when absent.
 * @returns {Date|null}
 */
export function computeBookingStartDate(bookingData, startTimeHours, merchantTimezone) {
  const base = bookingData?.startDate;
  if (!base) return null;
  if (bookingData.timing === 'immediate') {
    const immediate = new Date(base);
    return isNaN(immediate.getTime()) ? null : immediate;
  }

  if (startTimeHours == null || Number.isNaN(startTimeHours)) return null;

  const baseDate = new Date(base);
  if (isNaN(baseDate.getTime())) return null;

  // Delegate to the shared builder so timezone validation, minute-carry, and
  // invalid-result handling are identical to merchantLocalWallToUtc.
  const result = merchantLocalWallToUtc(baseDate, startTimeHours, merchantTimezone);
  return result && !isNaN(result.getTime()) ? result : null;
}

/**
 * Resolve the start-time-of-day (fractional hours) from a stored
 * `bookingData.startTime`.
 *
 * `startTime` is normally the time-slot object produced by TimeSelection,
 * which carries a numeric `hour` field alongside a `time` display string.
 * The numeric `hour` is the source of truth: it is derived from the
 * merchant's opening_hours and is language/locale independent.
 *
 * The display string, by contrast, is formatted with toLocaleTimeString in
 * the user's language locale, so its separator varies — e.g. Finnish renders
 * "8.00" / "14.30" rather than "8:00". Parsing that string with a colon-only
 * matcher silently failed and produced a bogus start time, which broke
 * availability checks for scheduled bookings in Finnish.
 *
 * Strategy: prefer the numeric `hour`; only parse the string as a fallback
 * (legacy/string startTime), and tolerate ":", "." and "," separators there.
 *
 * @param {Object|string|null} startTime
 * @returns {number|null} Fractional hours (e.g. 11.75), or null if unresolved.
 */
export function resolveStartTimeHours(startTime) {
  if (startTime == null) return null;

  if (typeof startTime === 'object'
      && typeof startTime.hour === 'number'
      && !Number.isNaN(startTime.hour)) {
    return startTime.hour;
  }

  const timeStr = typeof startTime === 'string' ? startTime : startTime.time;
  if (typeof timeStr !== 'string') return null;

  const ampm = timeStr.match(/(\d+)[:.,](\d+)\s*(AM|PM)/i);
  if (ampm) {
    let hours = parseInt(ampm[1], 10);
    const minutes = parseInt(ampm[2], 10);
    const meridiem = ampm[3].toUpperCase();
    if (meridiem === 'PM' && hours !== 12) hours += 12;
    else if (meridiem === 'AM' && hours === 12) hours = 0;
    return hours + minutes / 60;
  }

  const hm = timeStr.match(/(\d+)[:.,](\d+)/);
  if (hm) {
    return parseInt(hm[1], 10) + parseInt(hm[2], 10) / 60;
  }

  return null;
}

/**
 * Convert a merchant-local wall clock ("HH:mm" on the same calendar day as
 * `baseDate`, interpreted as browser-local) into a UTC Date, anchored to
 * the merchant's timezone. Useful for building batch-availability slot
 * boundaries from opening_hours strings.
 *
 * @param {Date} baseDate - Anchor calendar day (browser-local Y/M/D is used).
 * @param {number} hourFloat - Fractional hours (e.g. 9.5 for 09:30).
 * @param {string|null} merchantTimezone
 * @returns {Date}
 */
export function merchantLocalWallToUtc(baseDate, hourFloat, merchantTimezone) {
  const wall = buildWallClockString(baseDate, hourFloat);
  let result = fromZonedTime(wall, merchantTimezone || 'UTC');

  // date-fns-tz silently returns an Invalid Date when the timezone string is
  // malformed (trailing space, typo, abbreviation, hidden char). Recover by
  // re-anchoring in UTC so callers still get a usable instant instead of an
  // invalid date that detonates later in format()/toISOString().
  if (isNaN(result.getTime()) && merchantTimezone) {
    result = fromZonedTime(wall, 'UTC');
    if (!isNaN(result.getTime())) {
      captureWarning('merchantLocalWallToUtc: invalid timezone, fell back to UTC', {
        merchantTimezone, wall,
      }, { area: 'bookingDateTime', fn: 'merchantLocalWallToUtc' });
    }
  }

  // Still invalid → the base date or hour was unrecoverable. Report the inputs
  // so the originating bad value is visible in Sentry. The contract is
  // preserved (a Date is always returned); downstream guards handle NaN.
  if (isNaN(result.getTime())) {
    captureWarning('merchantLocalWallToUtc: produced invalid date', {
      baseDate: baseDate instanceof Date
        ? (isNaN(baseDate.getTime()) ? 'Invalid Date' : baseDate.toISOString())
        : String(baseDate),
      hourFloat,
      merchantTimezone,
      wall,
    }, { area: 'bookingDateTime', fn: 'merchantLocalWallToUtc' });
  }

  return result;
}

// Re-export for call sites that want zoned conversion without pulling in
// date-fns-tz directly.
export { fromZonedTime, toZonedTime };
